// src/Component/Sidebar.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Star,
  Clock,
  Layout,
  Goal,
  Share2,
  File,
  PieChart,
  Folder,
  Bell,
  Settings,
  ChevronDown,
  Plus,
  Headset,
  LogOut,
  StickyNote,
  User,
  Megaphone,
  Smartphone,
  Calendar,        // NEW: For Attendance
  ClipboardList,   // NEW: For Leave Management
  UserCheck,       // NEW: For Attendance Dashboard
  CalendarCheck,   // NEW: For Leave Status
} from 'lucide-react';
import { logout } from '../features/auth/authSlice';

// Role rank constants (matching backend)
const ROLE_RANKS = {
  'ADMIN': 5,
  'TECH': 5,
  'TEAM_LEADER': 4,
  'HR': 4,
  'SALES': 4,
  'QUALITY': 3,
  'SPA': 2,
  'PA': 1,
};

// Helper function to get user rank
const getUserRank = (user) => {
  if (!user || !user.employee_profile) return 0;
  const role = user.employee_profile.role;
  return ROLE_RANKS[role] || 0;
};

// Helper function to check if user is admin
const isAdmin = (user) => {
  return getUserRank(user) === 5 && user.employee_profile?.role === 'ADMIN';
};

// Helper function to check if user is team leader or above
const isTeamLeaderOrAbove = (user) => {
  return getUserRank(user) >= 4;
};


const styles = {
  sidebar: {
    width: '20%',
    minWidth: '60px',
    height: '100vh',
    transition: 'width 0.3s ease',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarCollapsed: {
    width: '60px',
  },
  nav: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    color: '#333',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    position: 'relative',
    textDecoration: 'none',
  },
  menuIcon: {
    width: '20px',
    height: '20px',
    marginRight: '12px',
  },
  menuLabel: {
    fontSize: '14px',
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  submenu: {
    marginLeft: '28px',
    paddingLeft: '12px',
    borderLeft: '1px solid #eee',
  },
  badge: {
    backgroundColor: '#ff4757',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '10px',
    fontSize: '12px',
    marginLeft: '8px',
  },
  newTag: {
    color: '#ff4757',
    fontSize: '12px',
    marginLeft: '8px',
  },
  collapseButton: {
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderTop: '1px solid #eee',
    transition: 'background-color 0.2s ease',
  },
  plusIcon: {
    marginLeft: 'auto',
    transition: 'transform 0.2s ease',
  },
};

const MenuItem = ({
  icon: Icon,
  label,
  badge,
  isNew,
  hasSubmenu,
  onClick,
  isOpen,
  isCollapsed,
  path,
}) =>
  path ? (
    <Link
      to={path}
      style={{ ...styles.menuItem, textDecoration: 'none', color: '#333' }}
      onClick={onClick}
    >
      <Icon style={styles.menuIcon} />
      {!isCollapsed && (
        <>
          <span style={styles.menuLabel}>{label}</span>
          {badge && <span style={styles.badge}>{badge}</span>}
          {isNew && <span style={styles.newTag}>New</span>}
          {hasSubmenu && (
            <Plus
              style={{
                ...styles.plusIcon,
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </>
      )}
    </Link>
  ) : (
    <div onClick={onClick} style={styles.menuItem}>
      <Icon style={styles.menuIcon} />
      {!isCollapsed && (
        <>
          <span style={styles.menuLabel}>{label}</span>
          {badge && <span style={styles.badge}>{badge}</span>}
          {isNew && <span style={styles.newTag}>New</span>}
          {hasSubmenu && (
            <Plus
              style={{
                ...styles.plusIcon,
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </>
      )}
    </div>
  );

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    storeInfo: false,
    employeeDetails: false,
    reports: false,
    alerts: false,
    attendance: false,      // NEW: For attendance submenu
    leaveManagement: false, // NEW: For leave management submenu
  });

  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user); // Get user from Redux state

  const toggleMenu = (menuId) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  // Get user role information
  const userIsAdmin = isAdmin(user);
  const userIsTeamLeaderOrAbove = isTeamLeaderOrAbove(user);

  const menuItems = [
    // 1) Dashboard
    {
      icon: Layout,
      label: 'Dashboard',
      path: '/',
      visible: true,
    },

    // 2) Store Info - Mixed visibility
    {
      icon: Folder,
      label: 'Store Info',
      id: 'storeInfo',
      hasSubmenu: true,
      visible: true,
      submenu: [
        {
          icon: File,
          label: 'Create Store',
          path: '/storemanagementapp',
          visible: userIsAdmin,
        },
        {
          icon: PieChart,
          label: 'Gas Station Dashboard',
          path: '/stores',
          visible: userIsAdmin,
        },
        {
          icon: File,
          label: 'Store Reports',
          path: '/storereportstable',
          visible: true,
        },
        {
          icon: StickyNote,
          label: 'Store Notes',
          path: '/store-notes',
          visible: true,
        },
        {
          icon: Megaphone,
          label: 'Announcements',
          path: '/announcements',
          visible: true,
        },
      ],
    },

    // 3) Employee Details - Team Leader and above
    {
      icon: Headset,
      label: 'Employee Details',
      id: 'employeeDetails',
      hasSubmenu: true,
      visible: userIsTeamLeaderOrAbove,
      submenu: [
        {
          icon: Star,
          label: 'Employee Cards',
          path: '/employeecards',
          visible: userIsTeamLeaderOrAbove,
        },
      ],
    },

    // 4) Reports - Always visible
    {
      icon: File,
      label: 'Reports',
      id: 'reports',
      hasSubmenu: true,
      visible: true,
      submenu: [
        {
          icon: File,
          label: 'Create Report Form',
          path: '/reports/new',
          visible: true,
        },
        {
          icon: Folder,
          label: 'Client Queries',
          path: '/clientqueriestable',
          visible: true,
        },
      ],
    },

    // 5) Alerts - Always visible
    {
      icon: Bell,
      label: 'Alerts',
      id: 'alerts',
      hasSubmenu: true,
      visible: true,
      submenu: [
        {
          icon: Clock,
          label: 'Suspicious Transaction Alerts',
          path: '/suspicioustransactionalerts',
          visible: true,
        },
      ],
    },

    // NEW: 6) Attendance System - Mixed visibility
    {
      icon: Calendar,
      label: 'Attendance',
      id: 'attendance',
      hasSubmenu: true,
      visible: true, // Visible to all (contains items for both employees and managers)
      submenu: [
        {
          icon: UserCheck,
          label: 'Attendance Dashboard',
          path: '/admin/attendance',
          visible: userIsTeamLeaderOrAbove, // Manager/Admin view
        },
        {
          icon: PieChart,
          label: 'Employee Summary',
          path: '/admin/summary',
          visible: userIsTeamLeaderOrAbove, // Manager/Admin view
        },
        {
          icon: Calendar,
          label: 'My Attendance',
          path: '/employee/attendance',
          visible: true, // All employees can view their own attendance
        },
      ],
    },

    // NEW: 7) Leave Management - Mixed visibility
    {
      icon: ClipboardList,
      label: 'Leave Management',
      id: 'leaveManagement',
      hasSubmenu: true,
      visible: true, // Visible to all (contains items for both employees and managers)
      submenu: [
        {
          icon: Plus,
          label: 'Apply Leave',
          path: '/employee/leave/apply',
          visible: true, // All employees can apply for leave
        },
        {
          icon: CalendarCheck,
          label: 'My Leave Status',
          path: '/employee/leave/status',
          visible: true, // All employees can view their leave status
        },
        {
          icon: ClipboardList,
          label: 'Manage Leaves',
          path: '/admin/leaves',
          visible: userIsTeamLeaderOrAbove, // Manager/HR can approve/reject leaves
        },
      ],
    },

    // 8) Device Management - Admin only
    {
      icon: Smartphone,
      label: 'Device Management',
      path: '/device-management',
      visible: userIsAdmin,
    },

    // 9) Profile - Always visible
    {
      icon: User,
      label: 'Profile',
      path: user?.employee_profile?.id ? `/employees/${user.employee_profile.id}` : '/employees',
      visible: true,
    },

    // 10) Logout - Always visible
    {
      icon: LogOut,
      label: 'Logout',
      onClick: handleLogout,
      visible: true,
    },
  ];

  return (
    <div
      style={{
        ...styles.sidebar,
        ...(isCollapsed && styles.sidebarCollapsed),
      }}
    >
      <nav style={styles.nav}>
        {menuItems.filter(item => item.visible !== false).map((item, index) => (
          <div key={index}>
            <MenuItem
              icon={item.icon}
              label={item.label}
              badge={item.badge}
              isNew={item.isNew}
              hasSubmenu={item.hasSubmenu}
              onClick={() => {
                if (item.hasSubmenu) {
                  toggleMenu(item.id);
                } else if (item.onClick) {
                  item.onClick();
                }
              }}
              isOpen={openMenus[item.id]}
              isCollapsed={isCollapsed}
              path={item.path}
            />
            {/* Render Submenu if exists and is open */}
            {!isCollapsed && item.hasSubmenu && openMenus[item.id] && (
              <div style={styles.submenu}>
                {item.submenu.filter(subItem => subItem.visible !== false).map((subItem, subIndex) => (
                  <div key={subIndex}>
                    <MenuItem
                      icon={subItem.icon}
                      label={subItem.label}
                      badge={subItem.badge}
                      isNew={subItem.isNew}
                      hasSubmenu={subItem.hasSubmenu}
                      onClick={() =>
                        subItem.hasSubmenu && toggleMenu(subItem.id)
                      }
                      isOpen={openMenus[subItem.id]}
                      isCollapsed={isCollapsed}
                      path={subItem.path}
                    />
                    {/* Render Nested Submenu if exists and is open */}
                    {!isCollapsed &&
                      subItem.hasSubmenu &&
                      openMenus[subItem.id] && (
                        <div style={styles.submenu}>
                          {subItem.submenu.map((nestedItem, nestedIndex) => (
                            <MenuItem
                              key={nestedIndex}
                              icon={nestedItem.icon}
                              label={nestedItem.label}
                              badge={nestedItem.badge}
                              isNew={nestedItem.isNew}
                              hasSubmenu={nestedItem.hasSubmenu}
                              onClick={nestedItem.onClick}
                              isOpen={openMenus[nestedItem.id]}
                              isCollapsed={isCollapsed}
                              path={nestedItem.path}
                            />
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Collapse button at the bottom */}
      <div
        style={styles.collapseButton}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <ChevronDown
          style={{
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

export default Sidebar;